import { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, KpiCard, SectionTitle } from "@/components/Kpi";
import { GestioneCqModal } from "@/components/GestioneCqModal";
import { eur, eurShort, num, MESI } from "@/lib/format";
import { usePalette } from "@/lib/palette";

const ANNO = 2026;
const GRIGIO = "#64748B", GRIGIO_CHIARO = "#CBD5E1";
const chiaro = (hex, q = 0.55) => {
  const n = parseInt(hex.slice(1), 16);
  const c = (v) => Math.round(v + (255 - v) * q).toString(16).padStart(2, "0");
  return `#${c(n >> 16)}${c((n >> 8) & 255)}${c(n & 255)}`;
};
const serieColori = (pal) => [
  { stack: "contattati", nome: "Clienti contattati", ant: GRIGIO, ag: GRIGIO_CHIARO },
  { stack: "caricate", nome: "CQ caricate", ant: pal.caricato, ag: chiaro(pal.caricato) },
  { stack: "liquidate", nome: "CQ liquidate", ant: pal.liquidato, ag: chiaro(pal.liquidato) },
];
const tot = (r, k) => (r[`${k}_ant`] || 0) + (r[`${k}_ag`] || 0);

function TooltipCq({ active, payload, label, SERIE, prefix, fmt }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg p-3 text-xs font-mono">
      <div className="font-bold text-slate-800 mb-2">{label}</div>
      {SERIE.map((s) => (
        <div key={s.stack} className="flex justify-between gap-4 py-0.5">
          <span className="font-semibold" style={{ color: s.ant }}>{s.nome}</span>
          <span>{fmt(tot(r, prefix + s.stack))} <span className="text-slate-400">(Antonella {fmt(r[`${prefix}${s.stack}_ant`])} · agenzia {fmt(r[`${prefix}${s.stack}_ag`])})</span></span>
        </div>
      ))}
    </div>
  );
}

function IstogrammaCq({ titolo, dati, SERIE, prefix = "", fmt, fmtLabel, testId }) {
  return (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">{titolo}</h3>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          {SERIE.map((s) => <span key={s.stack} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: s.ant }} /> {s.nome}</span>)}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">In <b>scuro</b> le pratiche recuperate da Antonella, in <b>chiaro</b> le pratiche segnalate dall'agenzia.</p>
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={dati} barGap={1} barCategoryGap="30%" maxBarSize={72} margin={{ top: 24, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace" }} />
          <YAxis allowDecimals={false} tickFormatter={fmtLabel} tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace" }} width={64} />
          <Tooltip content={<TooltipCq SERIE={SERIE} prefix={prefix} fmt={fmt} />} cursor={{ fill: "rgba(0,24,168,0.05)" }} />
          {SERIE.map((s) => [
            <Bar key={`${s.stack}-ant`} dataKey={`${prefix}${s.stack}_ant`} stackId={s.stack} fill={s.ant} isAnimationActive={false} />,
            <Bar key={`${s.stack}-ag`} dataKey={`${prefix}${s.stack}_ag`} stackId={s.stack} fill={s.ag} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              <LabelList valueAccessor={(e) => { const v = tot(e.payload ?? e, prefix + s.stack); return v ? fmtLabel(v) : ""; }} position="top"
                style={{ fontSize: 11, fontWeight: 700, fontFamily: "JetBrains Mono, monospace", fill: s.ant }} />
            </Bar>,
          ])}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

/** Report CQ: dashboard (pezzi e importi per mese) + elenco contatti + form "Gestione CQ". */
export default function DashboardCq() {
  const SERIE = serieColori(usePalette());
  const [meta, setMeta] = useState(null);
  const [agenzia, setAgenzia] = useState("");
  const [dash, setDash] = useState(null);
  const [modal, setModal] = useState({ open: false, contatto: null });

  useEffect(() => { api.get("/cq/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const carica = useCallback(() => {
    const q = `anno=${ANNO}${agenzia ? `&agenzia=${encodeURIComponent(agenzia)}` : ""}`;
    api.get(`/cq/report-dashboard?${q}`).then((r) => setDash(r.data)).catch((e) => toast.error(apiError(e)));
  }, [agenzia]);
  useEffect(() => { carica(); }, [carica]);

  const meseCorrente = new Date().getMonth() + 1;
  const mesi = (dash?.mesi || []).filter((m) => m.mese <= Math.max(meseCorrente, 1));
  const primoValorizzato = mesi.find((m) => tot(m, "contattati") > 0 || tot(m, "caricate") > 0 || tot(m, "liquidate") > 0)?.mese;
  const dati = mesi.filter((m) => primoValorizzato ? m.mese >= primoValorizzato : m.mese === meseCorrente)
    .map((m) => ({ ...m, label: MESI[m.mese - 1].slice(0, 3) }));
  const somma = (k) => dati.reduce((a, r) => a + (r[k] || 0), 0);
  const T = { contattati: somma("contattati_ant") + somma("contattati_ag"), contattati_ant: somma("contattati_ant"),
    caricate: somma("caricate_ant") + somma("caricate_ag"), caricate_ag: somma("caricate_ag"),
    liquidate: somma("liquidate_ant") + somma("liquidate_ag"), liquidate_ag: somma("liquidate_ag"),
    imp_caricate: somma("imp_caricate_ant") + somma("imp_caricate_ag"), imp_liquidate: somma("imp_liquidate_ant") + somma("imp_liquidate_ag") };

  return (
    <div data-testid="dashboard-cq">
      <SectionTitle sub="Clienti contattati, CQ caricate e liquidate · ogni barra distingue le pratiche recuperate da Antonella da quelle segnalate dall'agenzia"
        right={
          <div className="flex items-center gap-3">
            <select value={agenzia} onChange={(e) => setAgenzia(e.target.value)} data-testid="cq-filtro-agenzia"
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#0018A8]">
              <option value="">Tutte le agenzie</option>
              {(meta?.agenzie || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        }>
        Report CQ
      </SectionTitle>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard testId="cq-kpi-contattati" label="Clienti contattati" value={T.contattati} accent="slate"
          sub={`${num(T.contattati - T.contattati_ant)} segnalati dall'agenzia`} />
        <KpiCard testId="cq-kpi-caricate" label="CQ caricate" value={T.caricate} accent="caricato"
          sub={`${eurShort(T.imp_caricate)} · ${num(T.caricate_ag)} da segnalazione agenzia`} />
        <KpiCard testId="cq-kpi-liquidate" label="CQ liquidate" value={T.liquidate} accent="liquidato"
          sub={`${eurShort(T.imp_liquidate)} · ${num(T.liquidate_ag)} da segnalazione agenzia`} />
        <KpiCard testId="cq-kpi-conv-caricate" label="% caricate su contattati" value={T.contattati ? T.caricate / T.contattati * 100 : null} kind="perc" />
        <KpiCard testId="cq-kpi-quota-antonella" label="% recuperate da Antonella" kind="perc"
          value={T.contattati ? T.contattati_ant / T.contattati * 100 : null}
          sub={`${num(T.contattati_ant)} su ${num(T.contattati)} clienti contattati`} />
      </div>

      <div className="space-y-6">
        <IstogrammaCq titolo={`Report CQ · pezzi per mese ${ANNO}${agenzia ? ` · ${agenzia}` : ""}`} dati={dati} SERIE={SERIE}
          fmt={(v) => num(v || 0)} fmtLabel={(v) => num(v)} testId="cq-chart-pezzi" />
        <IstogrammaCq titolo={`Report CQ · importi per mese ${ANNO}${agenzia ? ` · ${agenzia}` : ""}`} dati={dati} SERIE={SERIE} prefix="imp_"
          fmt={(v) => eur(v || 0)} fmtLabel={(v) => eurShort(v)} testId="cq-chart-importi" />

      </div>

      <button type="button" onClick={() => setModal({ open: true, contatto: null })} data-testid="gestione-cq-btn"
        className="fixed bottom-6 right-6 z-[1100] inline-flex items-center gap-2 px-5 py-3 rounded-full
          bg-[#0F766E] text-white text-sm font-semibold shadow-lg shadow-teal-700/30
          hover:bg-[#0B5E58] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
        GESTIONE CQ
      </button>
      <GestioneCqModal open={modal.open} meta={meta} contatto={modal.contatto}
        onClose={() => setModal({ open: false, contatto: null })} onSaved={carica} />
    </div>
  );
}
